import { Fragment } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  BanknotesIcon,
  BuildingLibraryIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  StarIcon,
  ArrowPathIcon,
  ArrowUpTrayIcon,
  ReceiptRefundIcon,
  TagIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../stores/authStore';
import { useLogout } from '../../features/auth/api/useAuth';

interface MobileMenuDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

interface MenuItem {
  path: string;
  labelKey: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

const menuItems: MenuItem[] = [
  {
    path: '/transactions',
    labelKey: 'nav.transactions',
    icon: ReceiptRefundIcon,
  },
  {
    path: '/categories',
    labelKey: 'nav.categories',
    icon: TagIcon,
  },
  {
    path: '/savings',
    labelKey: 'nav.goals',
    icon: StarIcon,
  },
  {
    path: '/recurring',
    labelKey: 'nav.recurring',
    icon: ArrowPathIcon,
  },
  {
    path: '/debts',
    labelKey: 'nav.debts',
    icon: BanknotesIcon,
  },
  {
    path: '/accounts',
    labelKey: 'nav.accounts',
    icon: CreditCardIcon,
  },
  {
    path: '/banks',
    labelKey: 'nav.banks',
    icon: BuildingLibraryIcon,
  },
  {
    path: '/import',
    labelKey: 'nav.import',
    icon: ArrowUpTrayIcon,
  },
  {
    path: '/settings',
    labelKey: 'nav.settings',
    icon: Cog6ToothIcon,
  },
];

export function MobileMenuDrawer({ isOpen, onClose }: MobileMenuDrawerProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const logout = useLogout();

  const isActive = (path: string) => location.pathname.startsWith(path);

  const handleLogout = async () => {
    onClose();
    await logout.mutateAsync();
    navigate('/login');
  };

  const handleNavigation = (path: string) => {
    onClose();
    navigate(path);
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50 md:hidden" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-xs">
                  <div className="flex h-full flex-col bg-white shadow-xl">
                    {/* Header */}
                    <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-brand-purple-600 rounded-full flex items-center justify-center">
                            <span className="text-white font-semibold text-lg">
                              {user?.name?.charAt(0).toUpperCase() || 'U'}
                            </span>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-sm font-semibold text-gray-900">
                              {user?.name || t('nav.profile')}
                            </span>
                            <span className="text-xs text-gray-500">
                              {user?.email}
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 transition-colors"
                          onClick={onClose}
                          style={{ minHeight: '44px', minWidth: '44px' }}
                        >
                          <span className="sr-only">{t('nav.closeMenu')}</span>
                          <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                        </button>
                      </div>
                    </div>

                    {/* Navigation Items */}
                    <div className="flex-1 overflow-y-auto py-4">
                      <div className="space-y-1 px-3">
                        {menuItems.map((item) => {
                          const Icon = item.icon;
                          const active = isActive(item.path);

                          return (
                            <button
                              key={item.path}
                              onClick={() => handleNavigation(item.path)}
                              className={`w-full flex items-center space-x-3 rounded-lg px-4 py-3 text-left transition-colors ${
                                active
                                  ? 'bg-brand-purple-50 text-brand-purple-700'
                                  : 'text-gray-700 hover:bg-gray-100 active:bg-gray-200'
                              }`}
                              style={{ minHeight: '44px' }}
                            >
                              <Icon className="w-6 h-6 flex-shrink-0" />
                              <span className="text-base font-medium">
                                {t(item.labelKey)}
                              </span>
                            </button>
                          );
                        })}
                      </div>

                      {/* Divider */}
                      <div className="my-4 border-t border-gray-200" />

                      {/* Account Actions */}
                      <div className="space-y-1 px-3">
                        <button
                          onClick={() => handleNavigation('/settings')}
                          className="w-full flex items-center space-x-3 rounded-lg px-4 py-3 text-left text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                          style={{ minHeight: '44px' }}
                        >
                          <UserCircleIcon className="w-6 h-6 flex-shrink-0" />
                          <span className="text-base font-medium">{t('nav.profile')}</span>
                        </button>

                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center space-x-3 rounded-lg px-4 py-3 text-left text-error-600 hover:bg-error-50 active:bg-error-100 transition-colors"
                          style={{ minHeight: '44px' }}
                        >
                          <ArrowRightOnRectangleIcon className="w-6 h-6 flex-shrink-0" />
                          <span className="text-base font-medium">{t('nav.logout')}</span>
                        </button>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                          FinLab v1.0.0
                        </span>
                        <Link
                          to="/styleguide"
                          className="text-xs text-brand-purple-600 hover:text-brand-purple-700"
                          onClick={onClose}
                        >
                          {t('nav.styleGuide')}
                        </Link>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
